// =====================================================
// PORTFOLIO CRUD API + PROPERTY CRUD API + UNIT CRUD API
// =====================================================

import { z } from 'zod';
import { apiFetch } from './api.core';
import type { Portfolio } from './api.core';
import {
  MetadataSchemaFieldSchema,
  CreateUnitRequestSchema,
  UpdateUnitRequestSchema,
  type CreatePropertyRequest,
  type CreatePropertyResponse,
} from '@resida/api-types/leasing';

// =====================================================
// PORTFOLIO CRUD API
// =====================================================

export interface PortfolioWithStats extends Portfolio {
  propertyCount?: number;
  unitCount?: number;
}

export interface CreatePortfolioData {
  name: string;
}

export interface UpdatePortfolioData {
  name?: string;
}

/**
 * List all portfolios for the current manager
 */
export async function listPortfolios(): Promise<{ portfolios: PortfolioWithStats[] }> {
  return apiFetch<{ portfolios: PortfolioWithStats[] }>('/landlord/api/portfolios');
}

/**
 * Get a single portfolio by ID
 */
export async function getPortfolio(id: string): Promise<{ portfolio: PortfolioWithStats }> {
  return apiFetch<{ portfolio: PortfolioWithStats }>(`/landlord/api/portfolios/${id}`);
}

/**
 * Create a new portfolio
 */
export async function createPortfolio(data: CreatePortfolioData): Promise<{ portfolio: Portfolio }> {
  return apiFetch<{ portfolio: Portfolio }>('/landlord/api/portfolios', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing portfolio
 */
export async function updatePortfolio(id: string, data: UpdatePortfolioData): Promise<{ portfolio: Portfolio }> {
  return apiFetch<{ portfolio: Portfolio }>(`/landlord/api/portfolios/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a portfolio
 */
export async function deletePortfolio(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/portfolios/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// PROPERTY CRUD API
// =====================================================

export interface Property {
  id: string;
  portfolioId: string;
  name: string;
  propertyType: 'single_family' | 'multi_family';
  status: 'active' | 'inactive' | 'deleted';
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  // Legacy fields that might be in metadata
  yearBuilt?: number;
  squareFeet?: number;
}

export interface PropertyWithStats extends Property {
  unitCount?: number;
  availableUnitCount?: number;
  portfolio?: Portfolio;
  defaultUnitId?: string; // For single-family properties, the ID of the default unit
}

export interface PropertyFilters {
  portfolioId?: string;
  propertyType?: string;
}

export interface UpdatePropertyData {
  name?: string;
  propertyType?: Property['propertyType'];
  status?: 'active' | 'inactive';
  metadata?: Record<string, unknown>;
  defaultDepositAccountId?: string | null;
}

/**
 * List all properties, optionally filtered
 */
export async function listProperties(filters?: PropertyFilters): Promise<{ properties: PropertyWithStats[] }> {
  const params = new URLSearchParams();
  if (filters?.portfolioId) params.append('portfolioId', filters.portfolioId);
  if (filters?.propertyType) params.append('propertyType', filters.propertyType);
  const queryString = params.toString();
  const endpoint = queryString ? `/landlord/api/properties?${queryString}` : '/landlord/api/properties';
  return apiFetch<{ properties: PropertyWithStats[] }>(endpoint);
}

/**
 * Get a single property by ID
 */
export async function getProperty(id: string): Promise<{ property: PropertyWithStats }> {
  return apiFetch<{ property: PropertyWithStats }>(`/landlord/api/properties/${id}`);
}

/**
 * Create a new property. A single_family property carries its Unit's address and
 * details in `unit` and is created together with that Unit in this one request.
 */
export async function createProperty(data: CreatePropertyRequest): Promise<CreatePropertyResponse> {
  return apiFetch<CreatePropertyResponse>('/landlord/api/properties', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing property
 */
export async function updateProperty(id: string, data: UpdatePropertyData): Promise<{ property: Property }> {
  return apiFetch<{ property: Property }>(`/landlord/api/properties/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a property
 */
export async function deleteProperty(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/properties/${id}`, {
    method: 'DELETE',
  });
}

// =====================================================
// UNIT CRUD API
// =====================================================

export interface Unit {
  id: string;
  propertyId: string;
  unitNumber: string | null;
  propertyName?: string; // Included in list responses
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  bedrooms: number | null;
  bathrooms: string | null; // Can be Decimal from Prisma
  sqft?: number | null;
  occupancy: 'available' | 'occupied';
  deletedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// Derived from MetadataSchemaFieldSchema in @resida/api-types/leasing — the same schema
// object apps/api's unit routes serialize the response through.
export type MetadataSchemaField = z.infer<typeof MetadataSchemaFieldSchema>;

export interface UnitWithRelations extends Unit {
  property?: PropertyWithStats;
  tenants?: TenantWithDetails[];
  metadataSchema?: {
    fields: MetadataSchemaField[];
    summary: {
      total: number;
      filled: number;
      missingRequired: number;
      missingRecommended: number;
    };
  };
}

export interface UnitFilters {
  propertyId?: string;
  portfolioId?: string;
  minBedrooms?: number;
}

// Derived from CreateUnitRequestSchema in @resida/api-types/leasing — the same schema
// object apps/api's unit routes validate the request body through.
export type CreateUnitData = z.infer<typeof CreateUnitRequestSchema>;

// Derived from UpdateUnitRequestSchema in @resida/api-types/leasing — the same schema
// object apps/api's unit routes validate the request body through. Note: unitNumber /
// address2 / bedrooms / bathrooms / sqft are nullable-and-optional in the schema (unlike
// this type's prior hand-declared optional-only fields) — no current caller passes null
// explicitly (only omits the field), so the widened type is a no-op change.
export type UpdateUnitData = z.infer<typeof UpdateUnitRequestSchema>;

// Forward declaration needed by UnitWithRelations — defined in lease.api.ts
// We use a minimal type here to avoid circular imports
export interface TenantWithDetails {
  id: string;
  unitId: string | null;
  portfolioId: string;
  identityId: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: 'pending' | 'active' | 'inactive' | 'archived';
  archivedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  unit?: Unit;
  invites?: TenantInvite[];
}

export interface TenantInvite {
  id: string;
  token: string;
  tenantId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  unitId: string | null;
  portfolioId: string | null;
  roles: string | null;
  expiresAt: string;
  consumedAt: string | null;
  revokedAt: string | null;
  status: 'pending' | 'consumed' | 'revoked' | 'expired';
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * List all units, optionally filtered
 */
export async function listUnits(filters?: UnitFilters): Promise<{ units: UnitWithRelations[] }> {
  const params = new URLSearchParams();
  if (filters?.propertyId) params.append('propertyId', filters.propertyId);
  if (filters?.portfolioId) params.append('portfolioId', filters.portfolioId);
  if (filters?.minBedrooms) params.append('minBedrooms', String(filters.minBedrooms));
  const queryString = params.toString();
  const endpoint = queryString ? `/landlord/api/units?${queryString}` : '/landlord/api/units';
  return apiFetch<{ units: UnitWithRelations[] }>(endpoint);
}

/**
 * Get a single unit by ID
 */
export async function getUnit(id: string): Promise<{ unit: UnitWithRelations }> {
  return apiFetch<{ unit: UnitWithRelations }>(`/landlord/api/units/${id}`);
}

/**
 * Batch-resolve IANA timezones for a set of unit IDs.
 * Units that don't exist or aren't accessible to the caller are omitted from
 * the response — never defaulted. Backs useUnitTimezone (see hooks/useUnitTimezone.ts).
 */
export async function fetchUnitTimezones(unitIds: string[]): Promise<Record<string, string>> {
  const { timezones } = await apiFetch<{ timezones: Record<string, string> }>(
    '/landlord/api/units/timezones',
    {
      method: 'POST',
      body: JSON.stringify({ unitIds }),
    }
  );
  return timezones;
}

/**
 * Create a new unit
 */
export async function createUnit(data: CreateUnitData): Promise<{ unit: Unit }> {
  return apiFetch<{ unit: Unit }>('/landlord/api/units', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing unit
 */
export async function updateUnit(id: string, data: UpdateUnitData): Promise<{ unit: Unit }> {
  return apiFetch<{ unit: Unit }>(`/landlord/api/units/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a unit
 */
export async function deleteUnit(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/units/${id}`, {
    method: 'DELETE',
  });
}
